import axios from "axios";

/**
 * Back off politely when the server says we are asking too often.
 *
 * This logic was written for the home widgets and lived inside that one actions
 * file; it is here so every read can use it. Note it only ever acted on a 429 —
 * and until the backend was fixed to answer refusals with their real status, a
 * rate limit reached the app as a 500, so this never actually ran. It does now.
 *
 * `Retry-After` is preferred over any guess we could make, because the server
 * knows exactly when the window reopens. The exponential fallback exists only for
 * the case where the header is missing.
 */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** "Not there for a moment" — a deploy reloading PHP, not a bad request. */
const TRANSIENT_STATUSES = [502, 503, 504];

export const getRetryAfterMs = (err) => {
  const header =
    err?.response?.headers?.["retry-after"] ??
    err?.response?.headers?.["Retry-After"];
  if (!header) return null;

  // retry-after is either a number of seconds or an HTTP-date.
  const asNumber = Number(header);
  if (!Number.isNaN(asNumber) && Number.isFinite(asNumber)) {
    return Math.max(0, asNumber * 1000);
  }

  const asDate = Date.parse(header);
  if (!Number.isNaN(asDate)) {
    return Math.max(0, asDate - Date.now());
  }

  return null;
};

export async function axiosGetWith429Retry(
  url,
  config,
  // `request` exists so the retry policy can be exercised without a network:
  // the interesting part of this function is which failures it repeats and how
  // long it waits, and that is worth a test.
  { retries = 2, baseDelayMs = 500, request = (u, c) => axios.get(u, c) } = {},
) {
  let attempt = 0;

  while (true) {
    try {
      return await request(url, config);
    } catch (err) {
      const status = err?.response?.status;
      const rateLimited = status === 429;
      const transient = TRANSIENT_STATUSES.includes(status);

      // A rate limit and a server that has just blinked are both worth
      // repeating. Anything else is a real failure, and retrying it only spends
      // the allowance that got us here.
      if ((!rateLimited && !transient) || attempt >= retries) throw err;

      const backoffMs = Math.min(8000, baseDelayMs * Math.pow(2, attempt));

      const retryMs = rateLimited
        ? (getRetryAfterMs(err) ?? backoffMs)
        // A deploy ends for every client at once; jitter keeps them from all
        // coming back in the same millisecond.
        : backoffMs + Math.random() * backoffMs * 0.5;

      await sleep(retryMs);
      attempt += 1;
    }
  }
}
